import { useEffect } from "react";
import { create } from "zustand";
import { useTranslations } from "@acto/web-common/locales";
import { useRecognizedCustomers } from "@acto/taskpane/core/data-access/customers/useRecognizedCustomers";
import { useOpenCustomer } from "@acto/taskpane/field-sales-2/navigation";
import { CustomerSection } from "@acto/taskpane/field-sales-2/components/patterns/CustomerSection";
import { ScrollPageTitle } from "@acto/taskpane/field-sales-2/components/primitives/ScrollPage";

interface RecognizedNavigationState {
  lastNavigatedCustomerId: string | null;
  navigateOnce: (customerId: string, open: (customerId: string) => void) => void;
}

// Module-level so the once-per-customer guard survives the section remounting as the user
// moves between Home and the customer list; a per-instance ref would re-fire the redirect.
const useRecognizedNavigationStore = create<RecognizedNavigationState>((set, get) => ({
  lastNavigatedCustomerId: null,
  navigateOnce: (customerId, open) => {
    if (get().lastNavigatedCustomerId === customerId) return;
    set({ lastNavigatedCustomerId: customerId });
    open(customerId);
  },
}));

export function RecognizedCustomersSection() {
  const t = useTranslations("fieldSalesTwo");
  const openCustomer = useOpenCustomer();
  const navigateOnce = useRecognizedNavigationStore((s) => s.navigateOnce);
  const { customers, isFetching, isExactMatch } = useRecognizedCustomers();

  useEffect(() => {
    if (isExactMatch && customers.length === 1 && !isFetching) {
      navigateOnce(customers[0].id, openCustomer);
    }
  }, [isExactMatch, customers, isFetching, navigateOnce, openCustomer]);

  if (isFetching || customers.length === 0) {
    return null;
  }

  return (
    <>
      <ScrollPageTitle>{t("home.recognizedTitle")}</ScrollPageTitle>
      <CustomerSection customers={customers} isLoading={false} onOpenCustomer={(c) => openCustomer(c.id)} />
    </>
  );
}
